import { useEffect, useState } from "react";
import type { SiteReadyResponse } from "@hatch/shared";
import { verifySiteFromBrowser } from "./verifySite.js";

/**
 * Poll until the deployed site is actually serving.
 *
 * The deploy reports a URL before the edge routes it, so framing on the
 * terminal event races DNS and the user sees the browser's error page until
 * they happen to reload at the right moment. The frontend cannot detect that
 * itself — the site is cross-origin — so it asks the API, which makes a real
 * request and reads a real status code.
 *
 * The API's answer is necessary and not sufficient. It is a different network
 * path with a different cache, and it goes on being right while this browser is
 * still being handed the platform's pre-deploy `404` for the bundles — or
 * Hatch's own warm-up placeholder, which is a complete site at the right
 * address that is not the user's. So a server-side yes only opens the second
 * gate: a rehearsal of the frame's own requests, from here, judged against the
 * build identity the API reports alongside its answer. See ./verifySite.ts.
 */

/**
 * Deliberately not a backoff.
 *
 * The gap being covered is seconds long and the user is staring at a loader for
 * every one of them; widening the interval would mean sitting on a live site.
 * Each poll costs one server-side GET, and only while this pane is open.
 */
const POLL_INTERVAL_MS = 2000;

/**
 * When to stop insisting and just show the frame.
 *
 * Past this point the readiness answer has stopped being useful — either the
 * deploy is genuinely broken or the probe is wrong about it — and an endless
 * loader is worse than a frame the user can judge for themselves, with the link
 * sitting right above it either way.
 */
const GIVE_UP_MS = 90_000;

/**
 * How long the browser-side check may hold back a frame the API calls ready.
 *
 * The check reads a cross-origin response, which it can only do because the
 * deploy target sends `Access-Control-Allow-Origin: *`. A site served from
 * somewhere that does not would fail it forever, for a reason that has nothing
 * to do with whether it renders. Past this window the API's answer stands on
 * its own, which is exactly the behaviour that existed before the check did.
 */
const BROWSER_VERIFY_BUDGET_MS = 15_000;

export type SiteReadiness = "checking" | "ready" | "gave-up";

export function useSiteReady(sessionId: string, siteUrl: string): {
  status: SiteReadiness;
  /** How long the site has been polled for, so the UI can change what it says. */
  waitedMs: number;
} {
  const [status, setStatus] = useState<SiteReadiness>("checking");
  const [waitedMs, setWaitedMs] = useState(0);

  useEffect(() => {
    // Nothing to check yet, and nothing to spend the give-up budget on. A
    // caller that mounts this before the deploy has reported an address would
    // otherwise burn the whole 90s on a build and arrive at the reveal already
    // timed out — with `status` sticky, the gate then passes without a single
    // probe having run. Callers should still mount this only once they have a
    // URL; this is the backstop for when they don't.
    if (status !== "checking" || !siteUrl) return;

    // A single async loop rather than an interval: an interval would stack
    // requests whenever a probe takes longer than the tick, which is precisely
    // what happens while the host is still timing out.
    const controller = new AbortController();
    let cancelled = false;
    const startedAt = Date.now();
    // When the API first said yes, so the browser-side check gets a budget of
    // its own rather than eating whatever is left of the overall wait.
    let serverReadyAt: number | null = null;

    const sleep = (ms: number) =>
      new Promise<void>((resolve) => {
        const timer = setTimeout(resolve, ms);
        controller.signal.addEventListener("abort", () => {
          clearTimeout(timer);
          resolve();
        });
      });

    void (async () => {
      while (!cancelled) {
        try {
          const res = await fetch(`/api/sessions/${sessionId}/site`, {
            credentials: "same-origin",
            cache: "no-store",
            signal: controller.signal,
          });
          if (cancelled) return;
          if (res.ok) {
            const body = (await res.json()) as SiteReadyResponse;
            if (cancelled) return;
            if (body.ready) {
              serverReadyAt ??= Date.now();
              // The rehearsal doubles as the repair: it re-fetches past the
              // browser's cache, so an entry poisoned by an earlier attempt on
              // this hostname is overwritten before the frame can ask for it.
              // The identity comes from this same response — the API is the
              // only thing that knows which build the address is meant to be
              // serving, and it is absent for a deploy it could not stamp.
              const servesHere = await verifySiteFromBrowser(
                siteUrl,
                controller.signal,
                body.buildId,
              );
              if (cancelled) return;
              if (servesHere || Date.now() - serverReadyAt >= BROWSER_VERIFY_BUDGET_MS) {
                setStatus("ready");
                return;
              }
            }
          }
          // A non-OK answer is treated exactly like `ready: false`. The site is
          // the deliverable; a hiccup on Hatch's own API is no reason to
          // declare the user's site unreachable.
        } catch {
          if (cancelled) return;
        }

        const waited = Date.now() - startedAt;
        setWaitedMs(waited);
        if (waited >= GIVE_UP_MS) {
          setStatus("gave-up");
          return;
        }
        await sleep(POLL_INTERVAL_MS);
      }
    })();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [sessionId, siteUrl, status]);

  return { status, waitedMs };
}

export const SITE_READY_POLL_INTERVAL_MS = POLL_INTERVAL_MS;
export const SITE_READY_GIVE_UP_MS = GIVE_UP_MS;
export const SITE_READY_BROWSER_VERIFY_BUDGET_MS = BROWSER_VERIFY_BUDGET_MS;
